import { useMemo, useState } from "react";

import { Button } from "@/components/ui/button";

export interface LayerRoleMatch {
  role: string;
  layer: string | null;
  confidence: number;
  candidates: string[];
}

export interface LayerMappingReviewData {
  fileName: string;
  classification: string;
  classificationConfidence: number;
  roles: Record<string, LayerRoleMatch>;
}

interface LayerMappingReviewProps {
  data: LayerMappingReviewData;
  saving?: boolean;
  onConfirm: (overrides: Record<string, string>) => void;
}

const REVIEW_ROLES = [
  { role: "plot", label: "Granica čestice" },
  { role: "kig_footprint", label: "KIG / tlocrtna projekcija" },
  { role: "regulation_line", label: "Regulacijski pravac" },
  { role: "green", label: "Zelena površina" },
  { role: "walls", label: "Zidovi / tlocrt" },
  { role: "rooms", label: "Prostorije" },
];

export function LayerMappingReview({ data, saving, onConfirm }: LayerMappingReviewProps) {
  const initial = useMemo(() => {
    const out: Record<string, string> = {};
    for (const item of REVIEW_ROLES) {
      const match = data.roles[item.role];
      if (match?.layer) out[item.role] = match.layer;
    }
    return out;
  }, [data.roles]);
  const [selected, setSelected] = useState(initial);

  const layers = useMemo(() => {
    const all = new Set<string>();
    for (const match of Object.values(data.roles)) {
      if (match.layer) all.add(match.layer);
      for (const candidate of match.candidates ?? []) all.add(candidate);
    }
    return [...all].sort((a, b) => a.localeCompare(b));
  }, [data.roles]);

  return (
    <div className="mt-4 w-full max-w-3xl rounded-lg border border-amber-200 bg-amber-50 p-4 text-left">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h4 className="text-sm font-bold text-stone-800">Provjeri značenje DXF layera</h4>
          <p className="text-xs text-stone-600">
            {data.fileName}: klasifikacija {data.classification} (
            {Math.round(data.classificationConfidence * 100)}%)
          </p>
        </div>
        <Button size="sm" type="button" onClick={() => onConfirm(selected)} disabled={saving}>
          {saving ? "Spremam…" : "Spremi i ponovno analiziraj"}
        </Button>
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        {REVIEW_ROLES.map((item) => {
          const match = data.roles[item.role];
          return (
            <label key={item.role} className="text-xs font-semibold text-stone-700">
              {item.label}
              <select
                className="mt-1 w-full rounded-md border border-stone-300 bg-white px-2 py-1.5 text-xs"
                value={selected[item.role] ?? ""}
                onChange={(event) =>
                  setSelected((prev) => ({ ...prev, [item.role]: event.target.value }))
                }
              >
                <option value="">Nije mapirano</option>
                {layers.map((layer) => (
                  <option key={layer} value={layer}>
                    {layer}
                  </option>
                ))}
              </select>
              {match && (
                <span className="mt-1 block font-normal text-stone-500">
                  Pouzdanost: {Math.round(match.confidence * 100)}%
                </span>
              )}
            </label>
          );
        })}
      </div>
    </div>
  );
}
